import {inject, Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {Observable, tap} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {SupportTicketDto} from "../dto/support-ticket.dto";
import {SupportStatusDto} from "../dto/support-status.dto";
import {UrlBuilder} from "../item/url.item";
import {map} from "rxjs/operators";
import {OrgDto} from "../dto/org.dto";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {SearchSupportTicketRes} from "../dto/search/search-support-ticket.res";
import {isDefined} from "../../libs/utils/epic.utils";
import {VoidDto} from "../dto/void.dto";

@Injectable({providedIn: 'root'})
export class SupportTicketRepo {

  private readonly supportStatus = '/v1/org/{orgId}/support/status';
  private readonly supportTicketRead = '/v1/org/{orgId}/support/{id}';
  private readonly supportTicketWrite = '/v1/org/{orgId}/support';
  private readonly supportTicketClose = '/v1/org/{orgId}/support/{id}/close';
  private readonly supportTicketReply = '/v1/org/{orgId}/support/{id}/reply';
  private readonly supportTicketSearch = '/v1/org/{orgId}/support/search';

  private readonly http = inject(HttpClient);

  public status(org: OrgDto): Observable<WrapperDto<SupportStatusDto>> {
    const url = UrlBuilder.builder().addPath(this.supportStatus).addParam('orgId', org.id).build();
    return this.http.get<WrapperDto<SupportStatusDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public getById(org: OrgDto, id: string): Observable<WrapperDto<SupportTicketDto>> {
    const url = UrlBuilder.builder().addPath(this.supportTicketRead).addParam('orgId', org.id).addParam('id', id).build();
    return this.http.get<WrapperDto<SupportTicketDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public create(org: OrgDto, ticket: SupportTicketDto, screenshot: Blob | undefined, attachments: File[]): Observable<WrapperDto<SupportTicketDto>> {
    const data = new FormData();
    data.append('medias', new Blob([JSON.stringify(ticket)], {type: 'application/json'}), 'AccSupportTicketDto.json');
    if (isDefined(screenshot)) {
      data.append('medias', screenshot, `screenshot-${Date.now()}.jpg`);
    }
    attachments.forEach((f) => data.append('medias', f, f.name));
    const url = UrlBuilder.builder().addPath(this.supportTicketWrite).addParam('orgId', org.id)
      .setContentTypeMultipartFormData().build();
    return this.http.post<WrapperDto<SupportTicketDto>>(url.getUrl(), data, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public close(org: OrgDto, id: string): Observable<WrapperDto<VoidDto>> {
    const url = UrlBuilder.builder().addPath(this.supportTicketClose).addParam('orgId', org.id).addParam('id', id).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public reply(org: OrgDto, id: string, dto: SupportTicketDto): Observable<WrapperDto<SupportTicketDto>> {
    const url = UrlBuilder.builder().addPath(this.supportTicketReply).addParam('orgId', org.id).addParam('id', id).build();
    return this.http.post<WrapperDto<SupportTicketDto>>(url.getUrl(), dto, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Search
  // ##########################################################################

  public search(org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchSupportTicketRes>> {
    const url = UrlBuilder.builder().addPath(this.supportTicketSearch).addParam('orgId', org.id).build();
    return this.http.post<WrapperDto<SearchSupportTicketRes>>(url.getUrl(), request, url.getOptions()).pipe(
      tap((res) => res.dataType = res.dataType === 'SearchResultDto' ? 'SearchSupportTicketRes' : res.dataType),
      map((res) => new WrapperDto(res))
    );
  }
}
